import getBlockSendersConfirmTitleAndSubtext from './getBlockSendersConfirmTitleAndSubtext';
import blockSenderService from '../../services/blockSenderService';
import { confirm, DialogResponse } from 'owa-confirm-dialog';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import { Checkbox } from 'office-ui-fabric-react/lib/Checkbox';
import * as React from 'react';
import addDomainsToBlockedList from '../actionCreators/addDomainsToBlockedList';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { getMailTipsService, getMailTipsInformationFromXML } from 'owa-mail-tips';
import { sameOrgTitle, sameOrgSubtext } from './blockLastSendersHelper.locstring.json';
import loc from 'owa-localize';
import { isFeatureEnabled } from 'owa-feature-flags';
import { logUsage } from 'owa-analytics';

let blockDomain = false;

/**
 * Shows the confirm dialog before sending out the block service call
 * @param mailboxes to block
 * @param blockActionCallback to be called upon sending out the service call
 */
export default async function blockLastSendersHelper(
    mailboxes: EmailAddressWrapper[],
    blockActionCallback: () => void
) {
    if (!mailboxes) {
        // Do nothing in case of invalid mailboxes,
        // i.e mailboxes is null when user tries to block self
        return;
    }

    const isAnyMailboxInOrg = await isAnyMailboxInOrganization(mailboxes);
    if (isAnyMailboxInOrg) {
        await confirm(loc(sameOrgTitle), loc(sameOrgSubtext), false /* resolveImmediately */, {
            hideCancelButton: true,
        });
        return;
    }

    // Start block domain checkbox as unchecked
    blockDomain = false;

    const domains = getUniqueDomains(mailboxes);
    const [title, subtext, domainSubtext] = getBlockSendersConfirmTitleAndSubtext(
        mailboxes,
        domains
    );
    const checkBox = React.createElement(Checkbox, {
        key: 'blockDomainCheckbox',
        defaultChecked: blockDomain,
        label: domainSubtext,
        onChange: toggleBlockDomain,
    });
    const confirmResponse = await confirm(title, subtext, false /* resolveImmediately */, {
        bodyElement: isFeatureEnabled('tri-blockDomain') ? checkBox : null,
    });
    if (confirmResponse !== DialogResponse.ok) {
        return;
    }

    if (blockDomain) {
        addDomainsToBlockedList(domains);
        logUsage('BlockDomain', [domains.length]);
    }

    try {
        blockSenderService(mailboxes);
    } catch (error) {
        // VSO 19006: Handle service error caused by block
    }

    blockActionCallback();
}

function toggleBlockDomain() {
    blockDomain = !blockDomain;
}

function getUniqueDomains(mailboxes: EmailAddressWrapper[]): string[] {
    // Get all domains
    const domains = mailboxes.map((emailAddress: EmailAddressWrapper) => {
        return emailAddress.EmailAddress.split('@')[1];
    });
    // Remove duplicates
    return domains.filter((a, b) => domains.indexOf(a) === b);
}

async function isAnyMailboxInOrganization(mailboxes: EmailAddressWrapper[]): Promise<boolean> {
    // Do not check organization for consumer accounts
    if (isConsumer()) {
        return Promise.resolve(false);
    }
    const response = await getMailTipsService(
        getUserConfiguration().SessionSettings.UserEmailAddress,
        mailboxes.map(emailAddressWrapper => emailAddressWrapper.EmailAddress)
    );
    if (response) {
        const mailTipsInformationArray = response.ResponseMessages.map(singleResponseMessage => {
            return getMailTipsInformationFromXML(singleResponseMessage.MessageText);
        }).filter(value => !!value);
        for (const mailTipInfo of mailTipsInformationArray) {
            if (mailTipInfo.ExternalMemberCount === 0) {
                return Promise.resolve(true);
            }
        }
    }
    return Promise.resolve(false);
}
